/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";
import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { Nullable, Path, RMA, Setter } from "../../utils/Interfaces";
import { Box, Typography, Stack, Card, CardContent, CircularProgress, CardActionArea } from "@mui/material";

import { NavigateFunction, useNavigate, useSearchParams } from "react-router-dom";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { useAppDispatch } from "../../app/hooks";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { a11yTabProps } from "../../components/tabs/tabs";
import { getArchivedRMAs, getOpenRMAs } from "./materialsRMAFunctions";
import { DateRange, Person } from "@mui/icons-material";

export type RMAFunction = (dispatch: Dispatch<AnyAction>, setRmas: Setter<RMA[]>, setLoading: Setter<boolean>) => void;

const MaterialsRMAList = () => {
    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const navigate: NavigateFunction = useNavigate();

    const [loading, setLoading] = useState<boolean>(false);
    const [rmas, setRmas] = useState<RMA[]>([]);

    const [searchParams] = useSearchParams();
    const status: Nullable<string> = searchParams.get("status");

    function getRMAWithMutex(rmaFunction: RMAFunction, statusTemp: string): void {
        const initStatus: string = statusTemp;
        rmaFunction(
            dispatch,
            (rmasObj: RMA[]) => {
                if (status === initStatus) {
                    setRmas(rmasObj);
                }
            },
            setLoading
        );
    }

    useEffect(() => {
        getRMAWithMutex(status === "AR" ? getArchivedRMAs : getOpenRMAs, status ?? "OP");
    }, [status]);

    function statusToTabIndex(status: Nullable<string>): number {
        switch (status) {
            case "OP":
                return 0;
            case "AR":
                return 1;
            default:
                return 0;
        }
    }

    function tabIndexToStatus(index: number): string {
        switch (index) {
            case 0:
                return "OP";
            case 1:
                return "AR";
            default:
                return "OP";
        }
    }

    let breadcrumbPaths: Nullable<Path>[] = [
        { text: "Techwise Portal", route: "/" },
        { text: "Parts/Materials", route: "/?section=materials" },
        statusToTabIndex(status) === 0
            ? { text: "RMA - Open", route: "/materials/rma" }
            : { text: "RMA - Archive", route: "/materials/rma" },
    ];

    return (
        <Box>
            <StyledBreadcrumbs paths={breadcrumbPaths} />

            <Box sx={{ width: "100%" }}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs
                        value={statusToTabIndex(status)}
                        onChange={(event, newValue) => navigate(`/materials/rma?status=${tabIndexToStatus(newValue)}`)}
                        centered
                    >
                        <Tab label="Open" {...a11yTabProps(0)} />
                        <Tab label="Archive" {...a11yTabProps(1)} />
                    </Tabs>
                </Box>

                {loading && (
                    <Stack direction="row" justifyContent="center" width="100%" marginTop={20}>
                        <Stack direction="column" justifyContent="center">
                            <CircularProgress size={100} />
                        </Stack>
                    </Stack>
                )}

                {!loading && (
                    <Box>
                        {rmas.length === 0 && (
                            <Typography textAlign="center" sx={{ mt: 3 }}>
                                No RMAs found.
                            </Typography>
                        )}
                        {rmas.map((rma: RMA, index: number) => {
                            return (
                                <Card onClick={() => navigate(`/materials/rma/${rma.id}`)} sx={{ mt: 3 }}>
                                    <CardActionArea>
                                        <CardContent>
                                            <Typography variant="h5">{rma.title}</Typography>
                                            <Typography variant="subtitle1">{rma.description}</Typography>
                                            <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                                                <Stack direction="column" justifyContent="flex-start">
                                                    <Stack direction="row" justifyContent="flex-start">
                                                        <Person fontSize="small" />
                                                        <Typography variant="subtitle2" sx={{ ml: 1 }}>
                                                            {rma.reporter?.first_name ?? "--"}&nbsp;{rma.reporter?.last_name}
                                                        </Typography>
                                                    </Stack>
                                                    <Stack direction="row" justifyContent="flex-start">
                                                        <DateRange fontSize="small" />
                                                        <Typography variant="subtitle2" sx={{ ml: 1 }}>
                                                            {new Date(rma.date_opened).toLocaleDateString()}&nbsp;
                                                            {new Date(rma.date_opened).toLocaleTimeString()}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default MaterialsRMAList;
